/**
 * OneSignal Deferred Loading
 * 
 * Removes OneSignal from the critical rendering path by loading it
 * during browser idle time. This improves LCP by ~150-200ms.
 * 
 * @see src/docs/onesignal-sdk-loading-update.md
 */

declare global {
  interface Window {
    OneSignalDeferred?: Array<(OneSignal: any) => Promise<void>>;
  }
}

let oneSignalLoaded = false;

/**
 * Loads OneSignal SDK dynamically during browser idle time
 * This prevents the SDK from blocking the critical rendering path
 */
export function initOneSignalDeferred(): void {
  // Skip if already loaded or on server
  if (typeof window === 'undefined' || oneSignalLoaded) return;
  
  const loadOneSignal = () => {
    // Prevent double loading
    if (oneSignalLoaded) return;
    oneSignalLoaded = true;
    
    // 🛡️ FIX: Initialize OneSignalDeferred queue IMMEDIATELY (before script loads)
    // This allows notificationService.setExternalUserId() to push to the queue
    // even before the SDK script has finished loading, preventing 'tt' errors
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    
    // Create and append the script
    const script = document.createElement('script');
    script.src = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      // OneSignalDeferred already initialized above
      window.OneSignalDeferred.push(async (OneSignal: any) => {
        try {
          await OneSignal.init({
            appId: "a5a45208-7e2f-4d92-bf8c-07c1d8e8e5ff",
            notifyButton: {
              enable: true,
              size: 'medium',
              theme: 'default',
              position: 'bottom-left',
              showCredit: false,
              text: {
                'tip.state.unsubscribed': 'Attiva le notifiche',
                'tip.state.subscribed': "Notifiche attive",
                'tip.state.blocked': "Hai bloccato le notifiche",
                'message.prenotify': 'Clicca per attivare le notifiche sui prezzi',
                'message.action.subscribed': "Grazie! Riceverai aggiornamenti sui prezzi",
                'message.action.unsubscribed': "Non riceverai più notifiche",
                'dialog.main.title': 'Gestisci Notifiche',
                'dialog.main.button.subscribe': 'ATTIVA',
                'dialog.main.button.unsubscribe': 'DISATTIVA'
              }
            },
            allowLocalhostAsSecureOrigin: true
          });
          console.log('[OneSignal] Initialized successfully via deferred loading');
        } catch (error) {
          console.error('[OneSignal] Init error:', error);
        }
      });
    };
    
    script.onerror = () => {
      console.error('[OneSignal] Failed to load SDK');
      oneSignalLoaded = false; // Allow retry
    };
    
    document.body.appendChild(script);
  };
  
  // Use requestIdleCallback for optimal timing, with fallback to setTimeout
  // Timeout of 4000ms ensures it loads within a reasonable time even on slow devices
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(loadOneSignal, { timeout: 4000 });
  } else {
    // Fallback: load after 3 seconds to prioritize LCP
    setTimeout(loadOneSignal, 3000);
  }
}

/**
 * Check if OneSignal has been loaded
 */
export function isOneSignalLoaded(): boolean {
  return oneSignalLoaded;
}
